import { get as networkGet, post as networkPost } from '../../networkClient';

/**
 * Voice-call QA review (ZUM-3872).
 *
 * ZUM-3872 stops posting green calls to #ivr-call-recordings, so a green can no
 * longer be graded by reacting to a Slack message that was never posted. Looker
 * lists and filters every call and links here; this is the write path it cannot
 * provide.
 *
 * `useNewBaseUrl = true` routes to VITE_SERVER_URL_V2 (rb-backend-apis), whose
 * base already includes `/apis/v1`.
 */

/** The grade vocabulary. Must stay identical to the judge's — lowercase. */
export const HUMAN_GRADES = ['green', 'yellow', 'red'] as const;

export type HumanGrade = (typeof HUMAN_GRADES)[number];

/**
 * The flag vocabulary, matching the Slack channel's reaction key exactly.
 *
 * `silence_bug` is the team's 🔇 and means KELSEY went silent. It is NOT the
 * judge's `caller_went_quiet`, which counts the CALLER going quiet and Kelsey
 * re-prompting — the two were hand-checked over 20 calls and do not overlap.
 */
export const HUMAN_FLAGS = [
  { key: 'transferred', label: 'Transferred', emoji: '📲' },
  { key: 'tour_booked', label: 'Tour booked', emoji: '🎉' },
  { key: 'test_call', label: 'Test call', emoji: '❌' },
  { key: 'silence_bug', label: 'Kelsey went silent', emoji: '🔇' },
  { key: 'application_error', label: 'Application error', emoji: '🚫' },
  { key: 'prefer_to_text', label: 'Prefer to text', emoji: '📬' }
] as const;

export type HumanFlag = (typeof HUMAN_FLAGS)[number]['key'];

/**
 * The judge's row for a call, from `kelsey.voice_call_quality`.
 *
 * Mirrors `QUALITY_COLUMNS` in rb-backend-apis' `voiceCallAudit/repository.ts`
 * exactly. Keep it that way: a field the endpoint returns but this type omits is
 * still present at runtime (types are erased), so the gap shows up as a silently
 * untyped property rather than as an error — and with the compiler in the state
 * described in CLAUDE.md, not as an error at all.
 */
export interface VoiceCallJudge {
  call_sid: string;
  lead_id: string | null;
  client_id: string | null;
  community_id: string | null;
  created_at: string;
  graded_at: string | null;
  is_test: boolean;
  grade: HumanGrade | 'inconclusive' | 'test' | null;
  grade_mode: string | null;
  grade_scope: string | null;
  judge_confidence: number | null;
  judge_model: string | null;
  judge_prompt_version: string | null;
  diagnosis: string | null;
  categories: string[] | null;
  tags: string[] | null;
  n_turns: number;
  duration_s: number | null;
  ai_duration_s: number | null;
  transferred: boolean | null;
  /** Three-valued: null means Twilio had not reported the transfer leg yet. */
  transfer_answered: boolean | null;
  transfer_status: string | null;
  tour_booked: boolean | null;
  prefer_to_text: boolean | null;
  application_error: boolean | null;
  overtalk_turns: number;
  interrupted_turns: number;
  silence_watchdog_count: number;
  slow_ttft_turns: number;
  stt_provider: string | null;
}

/** A human verdict, from either the Slack channel or this screen. */
export interface VoiceCallHumanGrade {
  source: 'slack' | 'portal';
  grade: HumanGrade;
  provenance: string;
  notes: string | null;
  transferred?: boolean;
  tour_booked?: boolean;
  test_call?: boolean;
  silence_bug?: boolean;
  application_error?: boolean;
  prefer_to_text?: boolean;
}

export interface VoiceCallDetail {
  callSid: string;
  recordingUrl: string;
  judge: VoiceCallJudge;
  call: {
    client: string | null;
    community: string | null;
    clientId: string | null;
    communityId: string | null;
    leadId: string | null;
    callResult: string | null;
    callFrom: string | null;
    transcript: string | null;
    summary: string | null;
  };
  humanGrades: VoiceCallHumanGrade[];
  /** Whether this call came from the unbiased sample queue. Server-decided. */
  inReviewSample: boolean;
}

/**
 * What every helper here returns on failure.
 *
 * A discriminated union rather than a thrown error, because `networkClient`
 * hands errors back as values on the `get` path — see `getVoiceCallForReview`.
 */
export interface ApiError {
  error: true;
  status?: number;
  message: string;
}

export const isApiError = (value: unknown): value is ApiError =>
  !!value && typeof value === 'object' && (value as ApiError).error === true;

/**
 * Load one call for review.
 *
 * A 404 means the call has no judge row — an unknown CallSid, a legacy
 * zuma-voiceai call, or one that never reached StreamEnd.
 */
export async function getVoiceCallForReview(
  callSid: string
): Promise<VoiceCallDetail | ApiError> {
  const response: any = await networkGet(
    `/voice-call-audit/${encodeURIComponent(callSid)}`,
    null,
    null,
    true,
    true
  );
  // `networkGet` RESOLVES with the AxiosError rather than throwing, so the error
  // has to be detected on the returned value — a try/catch here would never fire
  // for a 404 or a network failure. Same shape as `messages.js` and
  // `sopAndAnnouncements.js`.
  if (response?.isAxiosError || response?.response) {
    return {
      error: true,
      status: response?.response?.status,
      message:
        response?.response?.data?.errorMessage ||
        response?.message ||
        'Could not load the call'
    };
  }
  return response?.data;
}

/**
 * Record this reviewer's verdict.
 *
 * `provenance` is deliberately NOT sent. The server decides it from whether the
 * call is in the hash-chosen review sample — a client that could name its own
 * would be able to file a self-selected grade as an unbiased one, and the
 * published judge-recall number is exactly what that distinction protects.
 */
export async function recordVoiceCallGrade(
  callSid: string,
  {
    grade,
    notes,
    flags
  }: {
    grade: HumanGrade;
    notes?: string | null;
    flags?: Partial<Record<HumanFlag, boolean>>;
  }
): Promise<VoiceCallHumanGrade | ApiError> {
  try {
    const response = await networkPost(
      `/voice-call-audit/${encodeURIComponent(callSid)}/human-grade`,
      { grade, notes: notes || null, flags: flags || {} },
      null,
      null,
      true
    );
    return response.data;
  } catch (error: any) {
    // `post` REJECTS on a non-2xx (unlike `get`, which resolves with the error),
    // and it has already dispatched the global error toast on the way out. All
    // that is left here is to tell the caller the save did not happen.
    return {
      error: true,
      status: error.response?.status,
      message:
        error.response?.data?.errorMessage ??
        error.response?.data?.message ??
        'Could not save the grade'
    };
  }
}
